<template>
  <Transition name="sticky-rise">
    <div
      v-if="visible"
      class="sticky-cta"
      role="complementary"
      :aria-label="$t('landing.sticky_cta_text')"
    >
      <span class="sticky-text">{{ $t('landing.sticky_cta_text') }}</span>
      <NuxtLink :to="localePath('/auth') + '?tab=signup'" class="btn-cyber-primary sticky-btn">
        {{ $t('landing.cta_signup') }}
      </NuxtLink>
      <button class="sticky-close" :aria-label="$t('landing.sticky_cta_dismiss')" @click="dismiss">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  </Transition>
</template>

<script setup>
// Mobile-only sticky signup bar. Appears once the visitor scrolls past the
// hero; never shows for logged-in users, never fights the docked audio
// player for the bottom edge, and can be dismissed for the session.
const localePath = useLocalePath()
const { loggedIn } = useAuth()
const { currentTrack } = useAudioPlayer()

const STORAGE_KEY = 'landing_sticky_cta_dismissed'

const pastHero = ref(false)
const dismissed = ref(false)

const visible = computed(() =>
  pastHero.value
  && !dismissed.value
  && !loggedIn.value
  && !currentTrack.value
)

const onScroll = () => {
  pastHero.value = window.scrollY > window.innerHeight * 0.85
}

const dismiss = () => {
  dismissed.value = true
  try { sessionStorage.setItem(STORAGE_KEY, '1') } catch {}
}

onMounted(() => {
  try { dismissed.value = sessionStorage.getItem(STORAGE_KEY) === '1' } catch {}
  window.addEventListener('scroll', onScroll, { passive: true })
  onScroll()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
})
</script>

<style scoped>
.sticky-cta {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 60; /* under the mobile menu (99) and audio player (9999) */
  display: none;
  align-items: center;
  gap: var(--space-3);
  padding:
    var(--space-3)
    calc(var(--space-4) + env(safe-area-inset-right, 0px))
    calc(var(--space-3) + env(safe-area-inset-bottom, 0px))
    calc(var(--space-4) + env(safe-area-inset-left, 0px));
  background: var(--glass-bg);
  backdrop-filter: blur(20px) saturate(140%);
  -webkit-backdrop-filter: blur(20px) saturate(140%);
  border-top: 1px solid var(--glass-border);
}

/* phones only */
@media (max-width: 767px) {
  .sticky-cta {
    display: flex;
  }
}

.sticky-text {
  flex: 1;
  min-width: 0;
  font-family: var(--font-heading);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--text-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sticky-btn {
  height: 44px !important;
  padding: 0 18px !important;
  font-size: 0.68rem !important;
  flex-shrink: 0;
}

.sticky-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-right: -8px;
  flex-shrink: 0;
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-muted);
}

.sticky-close:focus-visible,
.sticky-btn:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.sticky-rise-enter-active,
.sticky-rise-leave-active {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease;
}

.sticky-rise-enter-from,
.sticky-rise-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
